import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Plus, Users, Trash2, Mail, Phone, Search, Upload, Edit2, X, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const GuestList = ({ user }) => {
  const navigate = useNavigate();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sideFilter, setSideFilter] = useState('all'); // 'all', 'bride', 'groom'
  const [editingGuest, setEditingGuest] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef(null);
  const [newGuest, setNewGuest] = useState({
    name: '',
    email: '',
    phone: '',
    plus_one: false,
    dietary_restrictions: '',
    side: 'bride' // 'bride' or 'groom'
  });

  useEffect(() => {
    loadGuests();
  }, [user.id]);

  const loadGuests = async () => {
    try {
      const response = await axios.get(`${API}/guests/${user.id}`);
      setGuests(response.data);
    } catch (error) {
      console.error('Error loading guests:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter guests based on search and side filter
  const filteredGuests = guests.filter(guest => {
    const matchesSearch = guest.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (guest.email && guest.email.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesSide = sideFilter === 'all' || guest.side === sideFilter;
    return matchesSearch && matchesSide;
  });

  const handleAddGuest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/guests`, newGuest, {
        headers: { 'user-id': user.id }
      });
      toast.success('Guest added!');
      setNewGuest({ name: '', email: '', phone: '', plus_one: false, dietary_restrictions: '', side: 'bride' });
      setShowAddGuest(false);
      loadGuests();
    } catch (error) {
      toast.error('Failed to add guest');
    }
  };

  const handleUpdateGuest = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/guests/${editingGuest.id}`, editingGuest);
      toast.success('Guest updated!');
      setEditingGuest(null);
      loadGuests();
    } catch (error) {
      toast.error('Failed to update guest');
    }
  };

  const handleDeleteGuest = async (guestId) => {
    try {
      await axios.delete(`${API}/guests/${guestId}`);
      toast.success('Guest removed');
      loadGuests();
    } catch (error) {
      toast.error('Failed to remove guest');
    }
  };

  // Handle CSV/spreadsheet upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        // Skip header row
        const dataLines = lines.slice(1);
        let addedCount = 0;
        
        for (const line of dataLines) {
          const columns = line.split(',').map(col => col.trim().replace(/^"|"$/g, ''));
          if (columns.length >= 1 && columns[0]) {
            const guestData = {
              name: columns[0] || '',
              email: columns[1] || '',
              phone: columns[2] || '',
              side: (columns[3] || 'bride').toLowerCase(),
              plus_one: columns[4]?.toLowerCase() === 'yes' || columns[4]?.toLowerCase() === 'true',
              dietary_restrictions: columns[5] || ''
            };
            
            try {
              await axios.post(`${API}/guests`, guestData, {
                headers: { 'user-id': user.id }
              });
              addedCount++;
            } catch (err) {
              console.error('Failed to add guest:', guestData.name);
            }
          }
        }
        
        toast.success(`Successfully imported ${addedCount} guests!`);
        setShowUploadModal(false);
        loadGuests();
      } catch (error) {
        toast.error('Failed to parse file. Please check format.');
      }
    };
    reader.readAsText(file);
  };

  const totalGuestCount = guests.reduce((sum, guest) => sum + (guest.plus_one ? 2 : 1), 0);
  const brideCount = guests.filter(g => g.side === 'bride').reduce((sum, g) => sum + (g.plus_one ? 2 : 1), 0);
  const groomCount = guests.filter(g => g.side === 'groom').reduce((sum, g) => sum + (g.plus_one ? 2 : 1), 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      <div className="bg-white/70 backdrop-blur-xl border-b border-stone-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#57534E] hover:text-[#881337]"
          >
            <ArrowLeft className="mr-2" size={18} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-serif font-light tracking-tight text-[#1C1917] mb-2">
              Guest List
            </h1>
            <p className="text-[#57534E]">Manage RSVPs and guest information</p>
            <div className="flex gap-6 mt-4">
              <p className="text-2xl font-serif text-[#881337]">{totalGuestCount} Total</p>
              <p className="text-lg text-[#57534E]">Bride: {brideCount}</p>
              <p className="text-lg text-[#57534E]">Groom: {groomCount}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowUploadModal(true)}
              variant="outline"
              className="rounded-full border-[#881337] text-[#881337] hover:bg-rose-50"
            >
              <Upload className="mr-2" size={18} />
              Import
            </Button>
            <Button
              data-testid="add-guest-btn"
              onClick={() => setShowAddGuest(!showAddGuest)}
              className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
            >
              <Plus className="mr-2" size={18} />
              Add Guest
            </Button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <Input
              placeholder="Search guests by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-full bg-white border-stone-200"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={sideFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setSideFilter('all')}
              className={`rounded-full ${sideFilter === 'all' ? 'bg-[#881337] text-white' : 'border-stone-300'}`}
            >
              All
            </Button>
            <Button
              variant={sideFilter === 'bride' ? 'default' : 'outline'}
              onClick={() => setSideFilter('bride')}
              className={`rounded-full ${sideFilter === 'bride' ? 'bg-pink-500 text-white' : 'border-stone-300'}`}
            >
              Bride&apos;s Side
            </Button>
            <Button
              variant={sideFilter === 'groom' ? 'default' : 'outline'}
              onClick={() => setSideFilter('groom')}
              className={`rounded-full ${sideFilter === 'groom' ? 'bg-blue-500 text-white' : 'border-stone-300'}`}
            >
              Groom&apos;s Side
            </Button>
          </div>
        </div>

        {/* Add Guest Form */}
        {showAddGuest && (
          <form onSubmit={handleAddGuest} className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-white/50 mb-8 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#1C1917] mb-2 block font-medium">Guest Name</label>
                <Input
                  data-testid="guest-name-input"
                  value={newGuest.name}
                  onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })}
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0"
                  required
                />
              </div>
              <div>
                <label className="text-[#1C1917] mb-2 block font-medium">Email</label>
                <Input
                  data-testid="guest-email-input"
                  type="email"
                  value={newGuest.email}
                  onChange={(e) => setNewGuest({ ...newGuest, email: e.target.value })}
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#1C1917] mb-2 block font-medium">Phone</label>
                <Input
                  data-testid="guest-phone-input"
                  value={newGuest.phone}
                  onChange={(e) => setNewGuest({ ...newGuest, phone: e.target.value })}
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0"
                />
              </div>
              <div>
                <label className="text-[#1C1917] mb-2 block font-medium">Dietary Restrictions</label>
                <Input
                  data-testid="guest-dietary-input"
                  value={newGuest.dietary_restrictions}
                  onChange={(e) => setNewGuest({ ...newGuest, dietary_restrictions: e.target.value })}
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0"
                />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <input
                  data-testid="guest-plus-one-checkbox"
                  type="checkbox"
                  id="plus_one"
                  checked={newGuest.plus_one}
                  onChange={(e) => setNewGuest({ ...newGuest, plus_one: e.target.checked })}
                  className="w-5 h-5 rounded border-stone-300 text-[#881337]"
                />
                <label htmlFor="plus_one" className="text-[#1C1917]">Plus One</label>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-[#1C1917] font-medium">Side:</label>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setNewGuest({ ...newGuest, side: 'bride' })}
                  className={`rounded-full ${newGuest.side === 'bride' ? 'bg-pink-500 text-white' : 'bg-stone-100 text-stone-600'}`}
                >
                  Bride
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setNewGuest({ ...newGuest, side: 'groom' })}
                  className={`rounded-full ${newGuest.side === 'groom' ? 'bg-blue-500 text-white' : 'bg-stone-100 text-stone-600'}`}
                >
                  Groom
                </Button>
              </div>
            </div>
            <div className="flex gap-3">
              <Button data-testid="save-guest-btn" type="submit" className="flex-1 rounded-full bg-[#881337] hover:bg-[#9F1239] text-white">
                Save Guest
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAddGuest(false)} className="rounded-full">
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Guest Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGuests.map((guest) => (
            <div
              key={guest.id}
              data-testid={`guest-card-${guest.id}`}
              className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/50 hover:shadow-lg transition-all duration-300 relative"
            >
              <div className="absolute top-4 right-4 flex gap-2">
                <button
                  onClick={() => setEditingGuest({ ...guest })}
                  className="text-stone-400 hover:text-[#881337] transition-colors"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  data-testid={`delete-guest-${guest.id}-btn`}
                  onClick={() => handleDeleteGuest(guest.id)}
                  className="text-stone-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-xl font-serif text-[#1C1917]">{guest.name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  guest.side === 'groom' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'
                }`}>
                  {guest.side === 'groom' ? 'Groom' : 'Bride'}
                </span>
              </div>
              
              {guest.email && (
                <div className="flex items-center gap-2 text-sm text-[#57534E] mb-2">
                  <Mail size={14} />
                  {guest.email}
                </div>
              )}
              
              {guest.phone && (
                <div className="flex items-center gap-2 text-sm text-[#57534E] mb-2">
                  <Phone size={14} />
                  {guest.phone}
                </div>
              )}
              
              <div className="flex gap-2 mt-4">
                {guest.plus_one && (
                  <span className="text-xs bg-rose-50 text-[#881337] px-3 py-1 rounded-full">+1</span>
                )}
                <span className={`text-xs px-3 py-1 rounded-full ${
                  guest.rsvp_status === 'confirmed' ? 'bg-[#84A98C]/20 text-[#84A98C]' :
                  guest.rsvp_status === 'declined' ? 'bg-red-50 text-red-600' :
                  'bg-stone-100 text-stone-600'
                }`}>
                  {guest.rsvp_status === 'pending' ? 'Awaiting RSVP' : guest.rsvp_status}
                </span>
              </div>
              
              {guest.dietary_restrictions && (
                <p className="text-xs text-[#A8A29E] mt-3">
                  Dietary: {guest.dietary_restrictions}
                </p>
              )}
            </div>
          ))}
        </div>

        {filteredGuests.length === 0 && guests.length > 0 && (
          <div className="text-center py-16">
            <Search className="mx-auto text-stone-300 mb-4" size={64} />
            <p className="text-[#57534E]">No guests match your search or filter.</p>
          </div>
        )}

        {guests.length === 0 && (
          <div className="text-center py-16">
            <Users className="mx-auto text-stone-300 mb-4" size={64} />
            <p className="text-[#57534E] mb-6">No guests yet. Start building your guest list!</p>
            <Button
              onClick={() => setShowAddGuest(true)}
              className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
            >
              <Plus className="mr-2" size={18} />
              Add Your First Guest
            </Button>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#881337] to-[#9F1239] px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Upload className="text-white" size={20} />
                  </div>
                  <h3 className="font-serif font-medium text-white">Import Guest List</h3>
                </div>
                <button onClick={() => setShowUploadModal(false)} className="text-white/80 hover:text-white">
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-[#57534E] mb-4">
                Upload a CSV file with your guest list. The file should have columns in this order:
              </p>
              <div className="bg-stone-50 rounded-lg p-4 mb-6 font-mono text-xs">
                Name, Email, Phone, Side, Plus One, Dietary<br/>
                John Smith, john@email.com, 555-1234, bride, yes, vegetarian<br/>
                Jane Doe, jane@email.com, 555-5678, groom, no,
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="w-full rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
              >
                <Upload className="mr-2" size={18} />
                Choose File
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Guest Modal */}
      {editingGuest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#881337] to-[#9F1239] px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Edit2 className="text-white" size={20} />
                  </div>
                  <h3 className="font-serif font-medium text-white">Edit Guest</h3>
                </div>
                <button onClick={() => setEditingGuest(null)} className="text-white/80 hover:text-white">
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleUpdateGuest} className="p-6 space-y-4">
              <div>
                <label className="text-[#1C1917] mb-2 block text-sm font-medium">Name</label>
                <Input
                  value={editingGuest.name}
                  onChange={(e) => setEditingGuest({ ...editingGuest, name: e.target.value })}
                  className="rounded-lg"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[#1C1917] mb-2 block text-sm font-medium">Email</label>
                  <Input
                    type="email"
                    value={editingGuest.email || ''}
                    onChange={(e) => setEditingGuest({ ...editingGuest, email: e.target.value })}
                    className="rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-[#1C1917] mb-2 block text-sm font-medium">Phone</label>
                  <Input
                    value={editingGuest.phone || ''}
                    onChange={(e) => setEditingGuest({ ...editingGuest, phone: e.target.value })}
                    className="rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="text-[#1C1917] mb-2 block text-sm font-medium">Dietary Restrictions</label>
                <Input
                  value={editingGuest.dietary_restrictions || ''}
                  onChange={(e) => setEditingGuest({ ...editingGuest, dietary_restrictions: e.target.value })}
                  className="rounded-lg"
                />
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit_plus_one"
                    checked={editingGuest.plus_one}
                    onChange={(e) => setEditingGuest({ ...editingGuest, plus_one: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <label htmlFor="edit_plus_one" className="text-sm">Plus One</label>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Side:</label>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setEditingGuest({ ...editingGuest, side: 'bride' })}
                    className={`rounded-full text-xs ${editingGuest.side === 'bride' ? 'bg-pink-500 text-white' : 'bg-stone-100 text-stone-600'}`}
                  >
                    Bride
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setEditingGuest({ ...editingGuest, side: 'groom' })}
                    className={`rounded-full text-xs ${editingGuest.side === 'groom' ? 'bg-blue-500 text-white' : 'bg-stone-100 text-stone-600'}`}
                  >
                    Groom
                  </Button>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 rounded-full bg-[#881337] hover:bg-[#9F1239] text-white">
                  Save Changes
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditingGuest(null)} className="rounded-full">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestList;